/**
 * Page centrale "Tous les bons de chargement & déchargement"
 * PM Spec 05/05/2026 — /admin/loading-notes
 *
 * Fonctionnalités :
 *  - Recherche multi-filtres (type, statut, client, source, dates, recherche libre)
 *  - Sélection multiple + téléchargement ZIP
 *  - Export CSV
 *  - Téléchargement unitaire PDF
 *  - Lien direct vers l'opération/transfert source
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  FileText, Download, Archive, Loader2, Search, ExternalLink,
  ClipboardCheck, ClipboardList, Filter, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  signed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
};
const STATUS_LABEL = { signed: 'Signé', pending: 'En cours', draft: 'Brouillon' };

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return iso.slice(0, 10); }
};

const AdminLoadingNotesList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 25;

  // Filtres
  const [filters, setFilters] = useState({
    type: 'all', status: 'all', sourceKind: 'all',
    clientName: '', dateFrom: '', dateTo: '', search: '',
  });
  const [pendingSearch, setPendingSearch] = useState('');

  // Sélection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [downloadingZip, setDownloadingZip] = useState(false);

  const token = () => 'cookie-auth';
  const authHeader = { Authorization: `Bearer ${token()}` };

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== 'all') params.append(k, v);
      });
      params.append('skip', skip);
      params.append('limit', limit);
      const r = await axios.get(`${API}/loading-notes?${params}`, { headers: authHeader });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [filters, skip]); // eslint-disable-line

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const updateFilter = (k, v) => {
    setSkip(0);
    setFilters(f => ({ ...f, [k]: v }));
  };

  const resetFilters = () => {
    setSkip(0);
    setFilters({
      type: 'all', status: 'all', sourceKind: 'all',
      clientName: '', dateFrom: '', dateTo: '', search: '',
    });
    setPendingSearch('');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateFilter('search', pendingSearch.trim());
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(n => n.id)));
    }
  };

  const downloadPDF = async (note) => {
    const prefix = note.targetKind === 'transfer' ? 'transfers' : 'operations';
    try {
      const r = await axios.get(
        `${API}/${prefix}/${note.operationId}/loading-notes/${note.id}/pdf`,
        { responseType: 'blob', headers: authHeader }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${note.noteNumber}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Échec téléchargement PDF');
    }
  };

  const downloadZip = async () => {
    if (selectedIds.size === 0) return;
    setDownloadingZip(true);
    try {
      const r = await axios.post(
        `${API}/loading-notes/_download-zip`,
        { noteIds: Array.from(selectedIds) },
        { responseType: 'blob', headers: authHeader }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bons_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${selectedIds.size} bon(s) téléchargé(s)`);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur ZIP');
    } finally { setDownloadingZip(false); }
  };

  const exportCSV = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== 'all') params.append(k, v);
    });
    try {
      const r = await axios.get(
        `${API}/loading-notes/_export-csv?${params}`,
        { responseType: 'blob', headers: authHeader }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bons_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Échec export CSV');
    }
  };

  const goToSource = (note) => {
    if (note.targetKind === 'transfer') {
      navigate(`/admin/transfers/${note.operationId}`);
    } else {
      navigate(`/admin/operations/${note.operationId}`);
    }
  };

  const allChecked = items.length > 0 && selectedIds.size === items.length;
  const hasActiveFilters = filters.type !== 'all' || filters.status !== 'all'
    || filters.sourceKind !== 'all' || filters.clientName || filters.dateFrom
    || filters.dateTo || filters.search;

  return (
    <div className="p-6 space-y-4 max-w-[1400px] mx-auto" data-testid="loading-notes-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-emerald-600" />
            Bons de chargement & déchargement
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Vue centralisée — opérations spot et transferts contractuels
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
          {selectedIds.size > 0 && (
            <Button onClick={downloadZip} disabled={downloadingZip}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="download-zip-btn">
              {downloadingZip ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
              Télécharger ZIP ({selectedIds.size})
            </Button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                placeholder="Rechercher par n° bon, n° opération, plaque, chauffeur..."
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline">Rechercher</Button>
          </form>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <Select value={filters.type} onValueChange={(v) => updateFilter('type', v)}>
              <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="loading">Chargement</SelectItem>
                <SelectItem value="unloading">Déchargement</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => updateFilter('status', v)}>
              <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="signed">Signé</SelectItem>
                <SelectItem value="pending">En cours</SelectItem>
                <SelectItem value="draft">Brouillon</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.sourceKind} onValueChange={(v) => updateFilter('sourceKind', v)}>
              <SelectTrigger data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes sources</SelectItem>
                <SelectItem value="operation">Opération spot</SelectItem>
                <SelectItem value="transfer">Transfert</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="text" placeholder="Client (texte)"
              value={filters.clientName}
              onChange={(e) => updateFilter('clientName', e.target.value)}
              data-testid="filter-client"
            />
            <Input
              type="date" placeholder="Date début"
              value={filters.dateFrom}
              onChange={(e) => updateFilter('dateFrom', e.target.value)}
              data-testid="filter-date-from"
            />
            <Input
              type="date" placeholder="Date fin"
              value={filters.dateTo}
              onChange={(e) => updateFilter('dateTo', e.target.value)}
              data-testid="filter-date-to"
            />
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={resetFilters}
                    className="text-slate-600 h-7" data-testid="reset-filters-btn">
              <X className="w-3 h-3 mr-1" /> Réinitialiser les filtres
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <Checkbox checked={allChecked} onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox" />
              <span>{total} bon{total > 1 ? 's' : ''}</span>
              {selectedIds.size > 0 && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                  {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <div>Triés par date (plus récents d'abord)</div>
          </div>

          {loading ? (
            <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">
              <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              Aucun bon ne correspond à ces filtres.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-500 border-b">
                    <th className="px-4 py-2 text-left w-10"></th>
                    <th className="px-4 py-2 text-left">N° Bon</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Réf. opération</th>
                    <th className="px-4 py-2 text-left">Source</th>
                    <th className="px-4 py-2 text-left">Client</th>
                    <th className="px-4 py-2 text-left">Plaque</th>
                    <th className="px-4 py-2 text-left">Statut</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(n => (
                    <tr key={n.id} className="border-b hover:bg-slate-50/50"
                        data-testid={`note-row-${n.id}`}>
                      <td className="px-4 py-2">
                        <Checkbox
                          checked={selectedIds.has(n.id)}
                          onCheckedChange={() => toggleSelect(n.id)}
                          data-testid={`select-${n.id}`}
                        />
                      </td>
                      <td className="px-4 py-2 font-mono text-xs font-semibold">{n.noteNumber}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-1 text-xs">
                          {n.type === 'loading' ? (
                            <><ClipboardCheck className="w-3 h-3 text-blue-500" /> Chargement</>
                          ) : (
                            <><ClipboardList className="w-3 h-3 text-purple-500" /> Déchargement</>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">{formatDate(n.actualDate || n.createdAt)}</td>
                      <td className="px-4 py-2">
                        <button onClick={() => goToSource(n)}
                                className="text-emerald-700 hover:underline text-xs font-mono inline-flex items-center gap-1"
                                data-testid={`source-link-${n.id}`}>
                          {n.operationReference || '—'}
                          <ExternalLink className="w-3 h-3 opacity-60" />
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant="outline" className="text-[10px]">
                          {n.targetKind === 'transfer' ? 'Transfert' : 'Spot'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-xs">{n.clientName || '—'}</td>
                      <td className="px-4 py-2 text-xs font-mono">{n.licensePlate || '—'}</td>
                      <td className="px-4 py-2">
                        <Badge className={`text-[10px] border ${STATUS_BADGE[n.status]}`}>
                          {STATUS_LABEL[n.status]} ({n.signaturesCount}/2)
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => downloadPDF(n)}
                                data-testid={`download-pdf-${n.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="px-4 py-3 border-t flex items-center justify-between bg-slate-50">
              <span className="text-xs text-slate-500">
                {skip + 1}–{Math.min(skip + limit, total)} sur {total}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={skip === 0}
                        onClick={() => setSkip(Math.max(0, skip - limit))}>
                  Précédent
                </Button>
                <Button variant="outline" size="sm" disabled={skip + limit >= total}
                        onClick={() => setSkip(skip + limit)}>
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoadingNotesList;
